import React, { useEffect, useMemo, useState } from "react";
import { api, fmtErr, kg, money, num } from "@/lib/api";
import { Input } from "@/components/ui/input";
import DateDMY from "@/components/DateDMY";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import SearchableSelect from "@/components/SearchableSelect";
import { toast } from "sonner";
import { Plus, Trash2, Truck, Package, Layers } from "lucide-react";

const PACKING_WEIGHTS = { "25 Kg": 25, "1 Kg X 25": 25, "15 Kg Tin": 15, "20 Kg": 20, "10 Kg Jar": 10, "Loose": 1 };
const NO_VARIANT_PRODUCTS = new Set(["Desi Ghee", "White Butter", "Loose Ghee"]);
const NO_BRAND_PRODUCTS = { "Loose Ghee": "Loose" };
const uid = () => Math.random().toString(36).slice(2, 10);

const emptyBatch = () => ({ _id: uid(), batch_number: "", batch_quantity: "" });
const emptyProduct = () => ({ _id: uid(), product: "", brand: "", variant: "", packing: "", quantity: "", num_batches: "", batches: [] });
const emptyCustomer = () => ({
  _id: uid(),
  customer_id: "",
  invoice_number: "",
  lr_gr_number: "",
  products: [emptyProduct()],
});

export default function DispatchEntry() {
  const [products, setProducts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);

  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [vehicleId, setVehicleId] = useState("");
  const [approvedFreight, setApprovedFreight] = useState("");
  const [entries, setEntries] = useState([emptyCustomer()]);

  useEffect(() => {
    Promise.all([api.get("/products"), api.get("/vehicles"), api.get("/customers")])
      .then(([p, v, c]) => {
        setProducts(p.data.products);
        setVehicles(v.data.filter((x) => x.active));
        setCustomers(c.data.filter((x) => x.active));
      });
  }, []);

  const vehicle = vehicles.find((v) => v.id === vehicleId);
  const isZeroFreight = vehicle && (vehicle.type === "company" || vehicle.type === "customer");
  const catLabel = { transporter: "Transporter", company: "Company Transport", customer: "Customer Transport" };

  const productOf = (name) => products.find((p) => p.name === name);
  const brandOf = (pname, bname) => productOf(pname)?.brands.find((b) => b.name === bname);

  const weightOfLine = (p) => (PACKING_WEIGHTS[p.packing] || 0) * parseInt(p.quantity || 0, 10);
  const invoiceWeight = (e) => e.products.reduce((s, p) => s + weightOfLine(p), 0);
  const invoiceQty = (e) => e.products.reduce((s, p) => s + parseInt(p.quantity || 0, 10), 0);
  const tripWeight = useMemo(() => entries.reduce((s, e) => s + invoiceWeight(e), 0), [entries]);
  const costPerKg = useMemo(() => {
    if (isZeroFreight) return 0;
    const af = parseFloat(approvedFreight || 0);
    return tripWeight > 0 && af > 0 ? af / tripWeight : 0;
  }, [tripWeight, approvedFreight, isZeroFreight]);

  // -- entry mutations --
  const updateEntry = (id, patch) => setEntries((arr) => arr.map((e) => e._id === id ? { ...e, ...patch } : e));
  const updateProductLine = (eid, pid, patch) => setEntries((arr) => arr.map((e) => e._id !== eid ? e : {
    ...e, products: e.products.map((p) => p._id === pid ? { ...p, ...patch } : p)
  }));
  const addCustomer = () => setEntries((arr) => [...arr, emptyCustomer()]);
  const removeCustomer = (id) => setEntries((arr) => arr.length > 1 ? arr.filter((e) => e._id !== id) : arr);
  const addProductLine = (eid) => setEntries((arr) => arr.map((e) => e._id !== eid ? e : { ...e, products: [...e.products, emptyProduct()] }));
  const removeProductLine = (eid, pid) => setEntries((arr) => arr.map((e) => e._id !== eid ? e : {
    ...e, products: e.products.length > 1 ? e.products.filter((p) => p._id !== pid) : e.products
  }));

  const setNumBatches = (eid, pid, raw) => {
    const n = Math.max(0, parseInt(raw || 0, 10) || 0);
    setEntries((arr) => arr.map((e) => e._id !== eid ? e : {
      ...e,
      products: e.products.map((p) => {
        if (p._id !== pid) return p;
        const cur = p.batches || [];
        let next = cur.slice(0, n);
        while (next.length < n) next.push(emptyBatch());
        return { ...p, num_batches: raw === "" ? "" : String(n), batches: next };
      })
    }));
  };
  const updateBatch = (eid, pid, bid, patch) => setEntries((arr) => arr.map((e) => e._id !== eid ? e : {
    ...e,
    products: e.products.map((p) => p._id !== pid ? p : {
      ...p, batches: (p.batches || []).map((b) => b._id === bid ? { ...b, ...patch } : b)
    })
  }));

  const onProductChange = (eid, pid, product) => updateProductLine(eid, pid, { product, brand: "", variant: "", packing: NO_BRAND_PRODUCTS[product] || "" });
  const onBrandChange = (eid, pid, brand) => {
    const line = entries.find((e) => e._id === eid)?.products.find((p) => p._id === pid);
    const bo = brandOf(line.product, brand);
    updateProductLine(eid, pid, {
      brand,
      variant: bo && bo.variants.length === 1 ? bo.variants[0] : "",
      packing: bo && bo.packings.length === 1 ? bo.packings[0] : "",
    });
  };

  const reset = () => {
    setDate(new Date().toISOString().slice(0, 10));
    setVehicleId("");
    setApprovedFreight("");
    setEntries([emptyCustomer()]);
  };

  const submit = async () => {
    if (!vehicleId) return toast.error("Select a vehicle");
    if (!isZeroFreight && !approvedFreight) return toast.error("Approved Freight is required");
    if (entries.length === 0) return toast.error("Add at least one customer entry");

    for (const [ei, e] of entries.entries()) {
      if (!e.customer_id) return toast.error(`Customer #${ei + 1}: select a customer`);
      if (!e.invoice_number.trim()) return toast.error(`Customer #${ei + 1}: invoice number is required`);
      if (!e.lr_gr_number.trim()) return toast.error(`Customer #${ei + 1}: LR/GR number is required`);
      if (e.products.length === 0) return toast.error(`Customer #${ei + 1}: add at least one product line`);
      for (const [pi, p] of e.products.entries()) {
        if (!p.product || (!p.brand && !NO_BRAND_PRODUCTS[p.product]) || !p.packing || !p.quantity)
          return toast.error(`Customer #${ei + 1} · Line ${pi + 1}: fill product, brand, packing and quantity`);
        if (p.product === "SMP" && !p.variant)
          return toast.error(`Customer #${ei + 1} · Line ${pi + 1}: SMP requires variant`);
        if (p.product === "Dairy Whitener" && !p.variant)
          return toast.error(`Customer #${ei + 1} · Line ${pi + 1}: Dairy Whitener requires variant`);
        if (parseInt(p.quantity, 10) <= 0)
          return toast.error(`Customer #${ei + 1} · Line ${pi + 1}: quantity must be positive`);
        // Batch validation
        const batches = p.batches || [];
        for (const [bi, b] of batches.entries()) {
          if (!(b.batch_number || "").trim())
            return toast.error(`Customer #${ei + 1} · Line ${pi + 1} · Batch ${bi + 1}: batch number is required`);
        }
        const filledQtys = batches.filter((b) => b.batch_quantity !== "" && b.batch_quantity !== null && b.batch_quantity !== undefined);
        if (filledQtys.length > 0) {
          const total = batches.reduce((s, b) => s + (parseFloat(b.batch_quantity || 0) || 0), 0);
          if (Math.round(total * 10000) !== Math.round(parseFloat(p.quantity) * 10000))
            return toast.error(`Customer #${ei + 1} · Line ${pi + 1}: sum of batch quantities (${total}) must equal Qty (${p.quantity})`);
        }
      }
    }

    setBusy(true);
    try {
      const body = {
        date,
        vehicle_id: vehicleId,
        approved_freight: isZeroFreight ? 0 : parseFloat(approvedFreight || 0),
        customers: entries.map((e) => ({
          customer_id: e.customer_id,
          invoice_number: e.invoice_number.trim(),
          lr_gr_number: e.lr_gr_number.trim(),
          products: e.products.map((p) => ({
            product: p.product,
            brand: p.brand,
            variant: NO_VARIANT_PRODUCTS.has(p.product) ? null : p.variant,
            packing: p.packing,
            quantity: parseInt(p.quantity, 10),
            batches: (p.batches || []).map((b) => ({
              batch_number: (b.batch_number || "").trim(),
              batch_quantity: (b.batch_quantity === "" || b.batch_quantity === null || b.batch_quantity === undefined)
                ? null
                : parseFloat(b.batch_quantity),
            })),
          })),
        })),
      };
      const r = await api.post("/trips", body);
      toast.success(`Trip saved · ${r.data.lines_created} lines · ${kg(r.data.total_weight_kg)}`);
      reset();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const vehLabel = (v) => `${v.number || "(Company Transport)"} · ${catLabel[v.type]}`;

  return (
    <div className="space-y-6" data-testid="dispatch-entry-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Dispatch Entry</h1>
        <p className="text-slate-500 text-sm mt-1">One vehicle trip · Multiple customers · Multiple product lines. Records are locked once saved.</p>
      </div>

      {/* Vehicle / Trip header */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Truck className="h-5 w-5 text-blue-600" />
            <h2 className="font-heading font-semibold text-lg">Vehicle / Trip</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label>Date *</Label>
              <DateDMY value={date} onChange={setDate} testid="dispatch-date-input" />
            </div>
            <div className="lg:col-span-2">
              <Label>Vehicle Number *</Label>
              <SearchableSelect
                testid="dispatch-vehicle-select"
                value={vehicleId}
                onChange={setVehicleId}
                options={vehicles.map((v) => ({ value: v.id, label: vehLabel(v) }))}
                placeholder="Select vehicle"
              />
            </div>
          </div>

          {vehicle && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <InfoTile label="Transport Category" value={catLabel[vehicle.type]} testid="veh-category" />
              {vehicle.type === "transporter" && <InfoTile label="Transporter Name" value={vehicle.transporter_name || "—"} testid="veh-transporter" />}
              {vehicle.type !== "company" && (
                <>
                  <InfoTile label="Driver Name" value={vehicle.driver_name || "—"} testid="veh-driver" />
                  <InfoTile label="Contact Number" value={vehicle.contact_number || "—"} testid="veh-contact" />
                </>
              )}
            </div>
          )}

          <div className="mt-4">
            <Label>Approved Freight (₹) {!isZeroFreight && vehicle && "*"}</Label>
            <Input type="number" min="0" step="0.01"
              value={isZeroFreight ? "0" : approvedFreight}
              onChange={(e) => setApprovedFreight(e.target.value)}
              disabled={isZeroFreight || !vehicle}
              placeholder={isZeroFreight ? "₹0 (zero-freight vehicle)" : "e.g. 5000"}
              data-testid="dispatch-freight-input" />
            {isZeroFreight && <p className="text-xs text-slate-500 mt-1">Company & Customer Transport are zero-freight by rule.</p>}
          </div>
        </CardContent>
      </Card>

      {/* Customers section */}
      <div className="space-y-4">
        {entries.map((e, ei) => (
          <CustomerBlock
            key={e._id}
            index={ei}
            entry={e}
            customers={customers}
            productsMaster={products}
            onUpdate={(patch) => updateEntry(e._id, patch)}
            onRemove={() => removeCustomer(e._id)}
            onProductChange={(pid, v) => onProductChange(e._id, pid, v)}
            onBrandChange={(pid, v) => onBrandChange(e._id, pid, v)}
            onLineUpdate={(pid, patch) => updateProductLine(e._id, pid, patch)}
            onNumBatchesChange={(pid, v) => setNumBatches(e._id, pid, v)}
            onBatchUpdate={(pid, bid, patch) => updateBatch(e._id, pid, bid, patch)}
            onAddLine={() => addProductLine(e._id)}
            onRemoveLine={(pid) => removeProductLine(e._id, pid)}
            invoiceWeight={invoiceWeight(e)}
            invoiceQty={invoiceQty(e)}
            canRemove={entries.length > 1}
          />
        ))}
        <Button variant="outline" onClick={addCustomer} className="w-full border-dashed" data-testid="add-customer-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add Customer
        </Button>
      </div>

      {/* Trip summary */}
      <Card className="bg-slate-900 text-white">
        <CardContent className="p-5">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <Summary label="Customers" value={num(entries.length, 0)} />
            <Summary label="Product Lines" value={num(entries.reduce((s, e) => s + e.products.length, 0), 0)} />
            <Summary label="Total Vehicle Weight" value={kg(tripWeight)} accent />
            <Summary label="Transport Cost / Kg" value={money(costPerKg) + (costPerKg ? "/Kg" : "")} />
          </div>
          <div className="mt-5 flex justify-end items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-300 mr-auto" data-testid="dispatch-return-type-note">Return Type: <b>No Return</b> · record partial or full returns later from the Register</span>
            <Button variant="outline" onClick={reset} className="bg-transparent text-white border-white/30 hover:bg-white/10" data-testid="dispatch-reset-btn">
              Reset
            </Button>
            <Button onClick={submit} disabled={busy} className="bg-blue-500 hover:bg-blue-600" data-testid="dispatch-submit-btn">
              {busy ? "Saving…" : "Save & Lock Trip"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function InfoTile({ label, value, testid }) {
  return (
    <div className="rounded-lg border bg-slate-50 px-3 py-2" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-medium text-slate-900 text-sm truncate">{value}</div>
    </div>
  );
}

function Summary({ label, value, accent }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-blue-200">{label}</div>
      <div className={`font-heading font-mono-num font-bold mt-1 ${accent ? "text-2xl text-emerald-300" : "text-xl"}`}>{value}</div>
    </div>
  );
}

function CustomerBlock({
  index, entry, customers, productsMaster,
  onUpdate, onRemove, onProductChange, onBrandChange, onLineUpdate,
  onNumBatchesChange, onBatchUpdate,
  onAddLine, onRemoveLine, invoiceWeight, invoiceQty, canRemove,
}) {
  return (
    <Card className="border-slate-300" data-testid={`customer-block-${index}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-700 grid place-items-center font-semibold text-sm">{index + 1}</div>
            <h3 className="font-heading font-semibold">Customer #{index + 1}</h3>
            <Badge variant="outline" className="ml-2 font-mono-num">{kg(invoiceWeight)}</Badge>
          </div>
          {canRemove && (
            <Button variant="ghost" size="sm" onClick={onRemove} data-testid={`remove-customer-${index}`}>
              <Trash2 className="h-4 w-4 text-rose-600" />
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-1">
            <Label>Customer Name *</Label>
            <SearchableSelect
              testid={`customer-select-${index}`}
              value={entry.customer_id}
              onChange={(v) => onUpdate({ customer_id: v })}
              options={customers.map((c) => ({ value: c.id, label: c.name }))}
              placeholder="Select customer"
            />
          </div>
          <div>
            <Label>Invoice Number *</Label>
            <Input value={entry.invoice_number} onChange={(e) => onUpdate({ invoice_number: e.target.value.toUpperCase() })}
              className="uppercase" data-testid={`invoice-input-${index}`} placeholder="INV-0001" />
          </div>
          <div>
            <Label>LR/GR Number *</Label>
            <Input value={entry.lr_gr_number} onChange={(e) => onUpdate({ lr_gr_number: e.target.value })}
              data-testid={`lrgr-input-${index}`} />
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-2">POD is entered later from the POD Reconciliation screen once the driver returns with proof of delivery.</p>

        {/* Product lines */}
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Package className="h-4 w-4" /> Product Lines
          </div>
          {entry.products.map((p, pi) => (
            <ProductLine
              key={p._id}
              line={p}
              cIndex={index}
              pIndex={pi}
              productsMaster={productsMaster}
              onProductChange={(v) => onProductChange(p._id, v)}
              onBrandChange={(v) => onBrandChange(p._id, v)}
              onUpdate={(patch) => onLineUpdate(p._id, patch)}
              onNumBatchesChange={(v) => onNumBatchesChange(p._id, v)}
              onBatchUpdate={(bid, patch) => onBatchUpdate(p._id, bid, patch)}
              onRemove={() => onRemoveLine(p._id)}
              canRemove={entry.products.length > 1}
            />
          ))}
          <Button variant="outline" size="sm" onClick={onAddLine} className="border-dashed" data-testid={`add-product-btn-${index}`}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Add Product
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ProductLine({ line, cIndex, pIndex, productsMaster, onProductChange, onBrandChange, onUpdate, onNumBatchesChange, onBatchUpdate, onRemove, canRemove }) {
  const productObj = productsMaster.find((p) => p.name === line.product);
  const brandObj = productObj?.brands.find((b) => b.name === line.brand);
  const isNoVariant = NO_VARIANT_PRODUCTS.has(line.product);
  const isNoBrand = !!NO_BRAND_PRODUCTS[line.product];
  const w = (PACKING_WEIGHTS[line.packing] || 0) * parseInt(line.quantity || 0, 10);
  const batches = line.batches || [];
  const filledQtys = batches.filter((b) => b.batch_quantity !== "" && b.batch_quantity !== null && b.batch_quantity !== undefined);
  const totalBatchQty = batches.reduce((s, b) => s + (parseFloat(b.batch_quantity || 0) || 0), 0);
  const remaining = (parseFloat(line.quantity || 0) || 0) - totalBatchQty;
  const qtySumMismatch = filledQtys.length > 0 && Math.round(totalBatchQty * 10000) !== Math.round((parseFloat(line.quantity || 0) || 0) * 10000);

  return (
    <div className="p-3 bg-slate-50 rounded-lg space-y-3" data-testid={`product-line-${cIndex}-${pIndex}`}>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2 items-end">
        <div>
          <Label className="text-xs">Product *</Label>
          <Select value={line.product} onValueChange={onProductChange}>
            <SelectTrigger data-testid={`line-product-${cIndex}-${pIndex}`}><SelectValue placeholder="Product" /></SelectTrigger>
            <SelectContent>
              {productsMaster.map((p) => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Brand {!isNoBrand && "*"}</Label>
          <Select value={line.brand} onValueChange={onBrandChange} disabled={!line.product || isNoBrand}>
            <SelectTrigger data-testid={`line-brand-${cIndex}-${pIndex}`}><SelectValue placeholder={isNoBrand ? "None" : "Brand"} /></SelectTrigger>
            <SelectContent>
              {(productObj?.brands || []).filter((b) => b.name).map((b) => <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Variant {!isNoVariant && "*"}</Label>
          <Select value={line.variant} onValueChange={(v) => onUpdate({ variant: v })} disabled={isNoVariant || !line.brand}>
            <SelectTrigger data-testid={`line-variant-${cIndex}-${pIndex}`}>
              <SelectValue placeholder={isNoVariant ? "N/A" : "Variant"} />
            </SelectTrigger>
            <SelectContent>
              {(brandObj?.variants || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Packing *</Label>
          <Select value={line.packing} onValueChange={(v) => onUpdate({ packing: v })} disabled={!line.brand || isNoBrand}>
            <SelectTrigger data-testid={`line-packing-${cIndex}-${pIndex}`}><SelectValue placeholder="Packing" /></SelectTrigger>
            <SelectContent>
              {(brandObj?.packings || (isNoBrand ? [line.packing] : [])).map((pk) => <SelectItem key={pk} value={pk}>{pk}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Quantity *</Label>
          <Input type="number" min="1" step="1" value={line.quantity}
            onChange={(e) => onUpdate({ quantity: e.target.value })}
            data-testid={`line-qty-${cIndex}-${pIndex}`} />
          {w > 0 && <div className="text-[10px] text-emerald-700 mt-0.5 font-mono-num">{kg(w)}</div>}
        </div>
        <div>
          <Label className="text-xs"># Batches</Label>
          <Input type="number" min="0" step="1" value={line.num_batches ?? ""}
            onChange={(e) => onNumBatchesChange(e.target.value)}
            placeholder="0"
            data-testid={`line-numbatches-${cIndex}-${pIndex}`} />
        </div>
        <div className="flex justify-end">
          {canRemove && (
            <Button variant="ghost" size="icon" onClick={onRemove} data-testid={`remove-line-${cIndex}-${pIndex}`}>
              <Trash2 className="h-4 w-4 text-rose-600" />
            </Button>
          )}
        </div>
      </div>

      {batches.length > 0 && (
        <div className="rounded-md border border-slate-200 bg-white p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
              <Layers className="h-3.5 w-3.5" /> Batch Details
            </div>
            <div className="text-[11px] font-mono-num text-slate-500">
              Total: <b className={qtySumMismatch ? "text-rose-600" : "text-slate-700"}>{totalBatchQty || 0}</b>
              {" · "}Remaining: <b className={remaining < 0 ? "text-rose-600" : "text-slate-700"}>{isNaN(remaining) ? 0 : remaining}</b>
            </div>
          </div>
          <div className="space-y-2">
            {batches.map((b, bi) => (
              <div key={b._id} className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid={`batch-row-${cIndex}-${pIndex}-${bi}`}>
                <div>
                  <Label className="text-[11px]">Batch #{bi + 1} Number *</Label>
                  <Input value={b.batch_number || ""}
                    onChange={(e) => onBatchUpdate(b._id, { batch_number: e.target.value.toUpperCase() })}
                    placeholder="e.g. BATCH-001"
                    className="uppercase"
                    data-testid={`batch-number-${cIndex}-${pIndex}-${bi}`} />
                </div>
                <div>
                  <Label className="text-[11px]">Batch Quantity (optional)</Label>
                  <Input type="number" min="0" step="0.01" value={b.batch_quantity ?? ""}
                    onChange={(e) => onBatchUpdate(b._id, { batch_quantity: e.target.value })}
                    placeholder="Leave blank if unknown"
                    data-testid={`batch-quantity-${cIndex}-${pIndex}-${bi}`} />
                </div>
              </div>
            ))}
          </div>
          {qtySumMismatch && (
            <div className="mt-2 text-[11px] text-rose-600">
              Sum of batch quantities must equal Qty ({line.quantity}) or leave all batch quantities blank.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
